import { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { pt } from "date-fns/locale";
import { Search, FileText, Package2, Share2, Download, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/components/providers/AuthProvider";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { toast } from "@/components/ui/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

interface OrderProduct {
  id: number;
  name: string;
  quantity: number;
  unit: string;
}

interface Order {
  id: string;
  order_number: string;
  created_at: string;
  status: "pending" | "approved" | "rejected";
  products: OrderProduct[];
  total_items: number;
  observation?: string;
  department: string;
  items?: {
    product: {
      name: string;
    };
    quantity: number;
  }[];
}

const statusMap = {
  pending: { label: "Pendente", color: "bg-yellow-500" },
  approved: { label: "Aprovado", color: "bg-green-500" },
  rejected: { label: "Rejeitado", color: "text-white bg-red-500" },
};

const statusText = {
  pending: "Pendente",
  approved: "Aprovado",
  rejected: "Rejeitado",
};

export default function UserOrders() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  // Contadores de status
  const statusCounts = {
    total: orders.length,
    pending: orders.filter(order => order.status === "pending").length,
    approved: orders.filter(order => order.status === "approved").length,
    rejected: orders.filter(order => order.status === "rejected").length,
  };

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("orders")
        .select(`
          id,
          created_at,
          status,
          quantity,
          responsible,
          user_id,
          product:products(name),
          construction:constructions(name),
          department:departments(name)
        `)
        .eq("user_id", user?.id)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Erro ao carregar pedidos:", error);
        throw error;
      }

      console.log('Dados do banco:', data);

      const formattedOrders = data?.map(order => ({
        id: order.id,
        created_at: order.created_at,
        status: order.status.toLowerCase(),
        total_items: order.quantity,
        user_id: order.user_id,
        observation: `${order.product.name} - ${order.construction.name} (${order.department.name})`,
        items: [{
          product: {
            name: order.product.name
          },
          quantity: order.quantity
        }]
      })) || [];

      console.log('Pedidos formatados:', formattedOrders);
      setOrders(formattedOrders);
    } catch (error) {
      console.error("Erro ao carregar pedidos:", error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os pedidos",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.observation.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || order.status === statusFilter.toLowerCase();
    
    console.log('Filtrando pedido:', {
      id: order.id,
      status: order.status,
      filterStatus: statusFilter.toLowerCase(),
      matchesStatus,
      matchesSearch
    });
    
    return matchesSearch && matchesStatus;
  });

  const handleDeleteOrder = async () => {
    if (!selectedOrder) return;
    if (!user) {
      toast({
        title: "Erro",
        description: "Usuário não está autenticado",
        variant: "destructive",
      });
      return;
    }

    console.log('Tentando excluir pedido:', {
      orderId: selectedOrder.id,
      userId: user.id,
      selectedOrder
    });

    setLoading(true);
    try {
      const { error: orderError } = await supabase
        .from("orders")
        .delete()
        .eq("id", selectedOrder.id)
        .eq("user_id", user.id);

      if (orderError) {
        console.error("Error deleting order:", orderError);
        throw new Error(orderError.message || "Não foi possível excluir o pedido");
      }

      setOrders(orders.filter(order => order.id !== selectedOrder.id));
      toast({
        title: "Pedido excluído",
        description: `O pedido ${selectedOrder.order_number} foi excluído com sucesso.`,
      });

      setIsDeleteModalOpen(false);
    } catch (error) {
      console.error("Error in deletion process:", error);
      toast({
        title: "Erro",
        description: error instanceof Error ? error.message : "Não foi possível excluir o pedido. Por favor, tente novamente.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setSelectedOrder(null);
    }
  };

  const handleShareWhatsApp = (order: Order) => {
    const message = `*Pedido #${order.id}*\n` +
      `Data: ${format(new Date(order.created_at), "dd/MM/yyyy HH:mm", { locale: pt })}\n` +
      `Status: ${statusMap[order.status as keyof typeof statusMap].label}\n` +
      `Produto: ${order.items[0].product.name}\n` +
      `Quantidade: ${order.total_items}\n` +
      `Observação: ${order.observation || '-'}`;

    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleDownloadPDF = (order: Order) => {
    const doc = new jsPDF();

    // Adiciona o cabeçalho
    doc.setFontSize(20);
    doc.text("Detalhes do Pedido", 20, 20);

    // Informações básicas do pedido
    doc.setFontSize(12);
    doc.text(`Pedido #${order.id}`, 20, 40);
    doc.text(`Data: ${format(new Date(order.created_at), "dd/MM/yyyy HH:mm", { locale: pt })}`, 20, 50);
    doc.text(`Status: ${statusMap[order.status as keyof typeof statusMap].label}`, 20, 60);

    // Tabela de itens
    const tableData = order.items.map(item => [
      item.product.name,
      item.quantity.toString(),
    ]);

    autoTable(doc, {
      startY: 70,
      head: [["Produto", "Quantidade"]],
      body: tableData,
    });

    // Observações
    const finalY = (doc as any).lastAutoTable.finalY || 70;
    doc.text("Observações:", 20, finalY + 10);
    doc.text(order.observation || "-", 20, finalY + 20);

    // Download do PDF
    doc.save(`pedido-${order.id}.pdf`);
    
    toast({
      title: "PDF gerado com sucesso",
      description: "O download do seu pedido começará automaticamente",
    });
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex justify-between items-center flex-col sm:flex-row gap-4">
        <h1 className="text-3xl font-bold">Meus Pedidos</h1>
        <Button onClick={() => navigate("/orders/new")}>
          <Package2 className="w-4 h-4 mr-2" />
          Novo Pedido
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total de Pedidos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statusCounts.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-yellow-500">Pedidos Pendentes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statusCounts.pending}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-green-500">Pedidos Aprovados</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statusCounts.approved}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-red-500">Pedidos Rejeitados</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statusCounts.rejected}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Lista de Pedidos</CardTitle>
          <CardDescription>
            Visualize e acompanhe o status dos seus pedidos
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por produto..."
                className="pl-8"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Select
              value={statusFilter}
              onValueChange={setStatusFilter}
            >
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="pending">Pendente</SelectItem>
                <SelectItem value="approved">Aprovado</SelectItem>
                <SelectItem value="rejected">Rejeitado</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Itens</TableHead>
                  <TableHead>Observação</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-10">
                      Carregando pedidos...
                    </TableCell>
                  </TableRow>
                ) : filteredOrders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-10">
                      Nenhum pedido encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredOrders.map((order) => (
                    <TableRow key={order.id}>
                      <TableCell>
                        {format(new Date(order.created_at), "dd/MM/yyyy HH:mm", { locale: pt })}
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={cn(
                            order.status === "rejected" ? "bg-red-500 text-white hover:bg-red-600" :
                            order.status === "approved" ? "bg-green-500 text-white hover:bg-green-600" :
                            "bg-yellow-500 text-white hover:bg-yellow-600"
                          )}
                        >
                          {statusMap[order.status as keyof typeof statusMap].label}
                        </Badge>
                      </TableCell>
                      <TableCell>{order.total_items} itens</TableCell>
                      <TableCell className="max-w-[200px] truncate">
                        {order.observation || "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleShareWhatsApp(order)}
                          >
                            <Share2 className="h-4 w-4" />
                            <span className="sr-only">Compartilhar no WhatsApp</span>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownloadPDF(order)}
                          >
                            <Download className="h-4 w-4" />
                            <span className="sr-only">Baixar PDF</span>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedOrder(order);
                              setIsDeleteModalOpen(true);
                            }}
                          >
                            <Trash2 className="h-4 w-4 text-red-500 hover:text-red-700" />
                            <span className="sr-only">Excluir Pedido</span>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/orders/${order.id}`)}
                          >
                            <FileText className="h-4 w-4" />
                            <span className="sr-only">Ver detalhes</span>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Modal de Exclusão */}
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Excluir Pedido</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir o pedido {selectedOrder?.id}? Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteModalOpen(false)}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteOrder}
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Excluindo...
                </div>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Confirmar Exclusão
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
